import React, { useState, useMemo, useEffect } from "react";
import { ExpandMore as ExpandMoreIcon, HelpOutline as HelpOutlineIcon } from "@mui/icons-material";
import { Popover, Typography, Box, Divider } from "@mui/material";
import { UseCaseStep, TestTarget } from "../../types/TestTarget";
import { getActionConfig, getAvailableActions } from "../../utils/actionConfig";
import { getAvailableItems } from "./utils";
import { TargetSelectionModal } from "./TargetSelectionModal";
import { TargetDetailWindow } from "./TargetDetailWindow";

interface StepEditorProps {
  step: UseCaseStep;
  localTestTargets: any[];
  currentViewId?: string;
  mode?: "create" | "edit" | "view";
  layout?: "default" | "compact";
  stepIndex?: number;
  globalDropdownState?: {
    stepIndex: number | null;
    dropdownType: 'action' | 'parameter' | null;
    parameterName?: string;
  };
  onGlobalDropdownChange?: (state: {
    stepIndex: number | null;
    dropdownType: 'action' | 'parameter' | null;
    parameterName?: string;
  }) => void;
  onUpdateStep?: (field: keyof UseCaseStep, value: any) => void;
  onUpdateAction?: (action: string) => void;
  onUpdateParameter?: (paramKey: string, value: string) => void;
  onCreateTestTarget?: (callback?: (newTarget: any) => void) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const StepEditor: React.FC<StepEditorProps> = ({
  step,
  localTestTargets,
  currentViewId,
  mode = "edit",
  layout = "default",
  stepIndex,
  globalDropdownState,
  onGlobalDropdownChange,
  onUpdateStep,
  onUpdateAction,
  onUpdateParameter,
  onCreateTestTarget,
  className,
  style,
}) => {
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  // targetパラメータ用のモーダル状態管理
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [currentTargetParam, setCurrentTargetParam] = useState<string | null>(null);
  // アイテム詳細ウィンドウの状態管理
  const [openDetailWindow, setOpenDetailWindow] = useState<{
    testTarget: TestTarget | null;
    position: { x: number; y: number };
  }>({ testTarget: null, position: { x: 0, y: 0 } });
  // elementパラメータ用のコンボボックス状態管理
  const [elementInputValues, setElementInputValues] = useState<{
    [key: string]: string;
  }>({});
  const [showElementDropdowns, setShowElementDropdowns] = useState<{
    [key: string]: boolean;
  }>({});
  // アクションヘルプポップアップの状態管理
  const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLElement | null>(null);

  const availableItems = useMemo(() => {
    const items = getAvailableItems(localTestTargets, currentViewId);
    // 新規作成オプションを先頭に追加
    return [
      { id: "CREATE_NEW", name: "+ 新規テストターゲット作成", type: "create" },
      ...items,
    ];
  }, [localTestTargets, currentViewId]);

  // ヘルパー関数: targetパラメータの値から対応するテストターゲットを取得
  const getTargetFromParameter = (targetParamValue: string): any => {
    if (!targetParamValue) return null;
    
    // test_targets配列からidが一致するエントリを探す
    const targetEntry = step.action_detail?.test_targets?.find(
      (tt) => tt.id === targetParamValue
    );
    
    if (!targetEntry?.test_target_id) return null;
    
    // localTestTargetsから実際のテストターゲットを検索（再帰的に検索）
    const findTestTarget = (targets: any[], targetId: string): any => {
      for (const target of targets) {
        if (target.id === targetId) {
          return target;
        }
        if (target.children) {
          const found = findTestTarget(target.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findTestTarget(localTestTargets, targetEntry.test_target_id);
  };

  // ヘルパー関数: selectorがオブジェクトかどうかを判定
  const isSelectorObject = (selector: any): selector is Record<string, string> => {
    return (
      selector !== null &&
      typeof selector === "object" &&
      !Array.isArray(selector) &&
      typeof selector !== "string"
    );
  };

  // ヘルパー関数: selectorオブジェクトからキーのリストを取得
  const getSelectorKeys = (selector: any): string[] => {
    if (!isSelectorObject(selector)) return [];
    return Object.keys(selector);
  };

  /** valueType === 'number' のとき、半角数字（と allowNegative 時は先頭のマイナス）以外を除去 */
  const filterNumericInput = (value: string, allowNegative: boolean): string => {
    if (allowNegative && value.startsWith("-")) {
      return "-" + value.slice(1).replace(/[^0-9]/g, "");
    }
    return value.replace(/[^0-9]/g, "");
  };

  // elementパラメータの候補を取得
  const getElementCandidates = (): string[] => {
    // targetパラメータを探す
    const targetParam = step.action_detail?.step_action?.parameters.find(
      (p) => p.key === "target"
    );
    
    if (!targetParam?.value) return [];
    
    const testTarget = getTargetFromParameter(targetParam.value);
    if (!testTarget || testTarget.type !== "item" || !testTarget.params) return [];
    
    const itemParams = testTarget.params as any;
    if (!itemParams.selector) return [];
    
    return getSelectorKeys(itemParams.selector);
  };

  // グローバルプルダウン状態に基づいてローカル状態を制御
  useEffect(() => {
    if (globalDropdownState && stepIndex !== undefined) {
      // 他のステップでプルダウンが開かれた場合、このステップのプルダウンを閉じる
      if (globalDropdownState.stepIndex !== stepIndex && globalDropdownState.stepIndex !== null) {
        setShowActionDropdown(false);
      }
    }
  }, [globalDropdownState, stepIndex]);

  // 現在選択中のターゲットIDを取得
  const getSelectedTargetId = (): string | undefined => {
    if (!currentTargetParam) return undefined;
    const existingParam = step.action_detail?.step_action?.parameters.find(
      (p) => p.key === currentTargetParam
    );
    if (!existingParam?.value) return undefined;
    const targetEntry = step.action_detail?.test_targets?.find(
      (tt) => tt.id === existingParam.value
    );
    return targetEntry?.test_target_id;
  };

  // モーダルからターゲットを選択した時の処理
  const handleTargetSelect = (targetId: string) => {
    if (!currentTargetParam) return;

    // 通常のターゲット選択
    onUpdateParameter?.(currentTargetParam, targetId);
  };

  // アイテム詳細ウィンドウを開く
  const handleViewItem = (item: TestTarget) => {
    const baseX = window.innerWidth / 2 - 500; // モーダル幅の半分を引く
    const baseY = window.innerHeight / 2 - 350; // モーダル高さの半分を引く
    const position = {
      x: Math.max(0, baseX),
      y: Math.max(0, baseY),
    };

    setOpenDetailWindow({ testTarget: item, position });
  };

  // アイテム詳細ウィンドウを閉じる
  const handleCloseDetailWindow = () => {
    setOpenDetailWindow({ testTarget: null, position: { x: 0, y: 0 } });
  };

  // elementパラメータの入力値を初期化
  useEffect(() => {
    if (step.action_detail?.step_action?.parameters) {
      const newElementInputValues: { [key: string]: string } = {};
      step.action_detail.step_action.parameters.forEach((param) => {
        if (param.key === "element") {
          newElementInputValues[param.key] = param.value || "";
        }
      });
      setElementInputValues(newElementInputValues);
    }
  }, [step.action_detail?.step_action?.parameters]);

  // プルダウン外クリック時の処理（targetプルダウンは削除済み）
  // elementプルダウンの開閉状態をglobalDropdownStateに反映
  useEffect(() => {
    const hasOpenElementDropdown = Object.values(showElementDropdowns).some(Boolean);
    if (hasOpenElementDropdown && stepIndex !== undefined) {
      onGlobalDropdownChange?.({
        stepIndex,
        dropdownType: 'parameter',
        parameterName: Object.keys(showElementDropdowns).find(
          key => showElementDropdowns[key]
        ) || undefined
      });
    } else if (!showActionDropdown && !hasOpenElementDropdown) {
      onGlobalDropdownChange?.({
        stepIndex: null,
        dropdownType: null
      });
    }
  }, [showElementDropdowns, stepIndex, onGlobalDropdownChange, showActionDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // elementパラメータのinput要素やドロップダウン内のクリックは無視
      if (
        target.closest('input[type="text"]') ||
        target.closest('[data-element-dropdown]')
      ) {
        return;
      }
      setShowActionDropdown(false);
      setShowElementDropdowns({});
      // グローバル状態もリセット
      onGlobalDropdownChange?.({ stepIndex: null, dropdownType: null });
    };

    if (
      showActionDropdown ||
      Object.values(showElementDropdowns).some(Boolean)
    ) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showActionDropdown, showElementDropdowns, onGlobalDropdownChange, stepIndex]);

  // デバッグログ
  console.log('🔍 StepEditor render:', {
    stepIndex,
    mode,
    step: step.step?.substring(0, 30),
    has_action_detail: !!step.action_detail,
    has_test_targets: !!step.action_detail?.test_targets,
    test_targets_count: step.action_detail?.test_targets?.length || 0,
    test_targets: step.action_detail?.test_targets,
  });

  // 現在選択されているアクションの説明を取得
  const getCurrentActionDescription = (): string => {
    const currentAction = step.action_detail?.step_action?.action || "click";
    const actionConfig = getActionConfig(currentAction);
    return actionConfig?.description || "アクションの説明がありません。";
  };

  // モーダルとウィンドウのレンダリング部分
  const renderModalsAndWindows = () => (
    <>
      {/* ターゲット選択モーダル */}
      {mode === "edit" && (
        <TargetSelectionModal
          open={showTargetModal}
          onClose={() => {
            setShowTargetModal(false);
            setCurrentTargetParam(null);
            // モーダルを閉じる際に、開いている詳細ウィンドウも閉じる
            setOpenDetailWindow({ testTarget: null, position: { x: 0, y: 0 } });
          }}
          onSelect={handleTargetSelect}
          selectedTargetId={getSelectedTargetId()}
          localTestTargets={localTestTargets}
          currentViewId={currentViewId}
          onViewItem={handleViewItem}
          onCreateTestTarget={onCreateTestTarget}
        />
      )}

      {/* アイテム詳細ウィンドウ */}
      {openDetailWindow.testTarget && (
        <TargetDetailWindow
          open={true}
          onClose={handleCloseDetailWindow}
          testTarget={openDetailWindow.testTarget}
          testTargets={localTestTargets}
          position={openDetailWindow.position}
        />
      )}

      {/* アクションヘルプポップアップ */}
      <Popover
        open={Boolean(helpAnchorEl)}
        anchorEl={helpAnchorEl}
        onClose={() => setHelpAnchorEl(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        disablePortal={false}
        sx={{
          zIndex: 2000,
        }}
      >
        <Box sx={{ p: 2, maxWidth: 500, maxHeight: 600, overflowY: "auto" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            {step.action_detail?.step_action?.action || "click"} の説明
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
            {getCurrentActionDescription()}
          </Typography>
          {(() => {
            const currentAction = step.action_detail?.step_action?.action || "click";
            const actionConfig = getActionConfig(currentAction);
            if (!actionConfig?.parameters || actionConfig.parameters.length === 0) {
              return null;
            }
            return (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                  パラメータ
                </Typography>
                {actionConfig.parameters.map((param, index) => (
                  <Box key={index} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: "medium", mb: 0.5 }}>
                      {param.name}
                      {param.required && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "error.main", ml: 0.5 }}
                        >
                          (必須)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                      {param.description}
                    </Typography>
                  </Box>
                ))}
              </>
            );
          })()}
        </Box>
      </Popover>
    </>
  );

  if (mode === "view") {
    return (
      <>
        <div className={className} style={style}>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ color: "#64748b", marginRight: "8px" }}>
              操作内容:
            </span>
            <span>{step.step || "手順が入力されていません"}</span>
          {step.action_detail?.step_action && (
            <div
              style={{
                fontSize: "0.7rem",
                color: "#64748b",
                marginTop: "4px",
              }}
            >
              アクション: {step.action_detail.step_action.action}
              {step.action_detail.step_action.parameters.length > 0 && (
                <span>
                  {" "}
                  ({step.action_detail.step_action.parameters.length}
                  個のパラメータ)
                </span>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "8px" }}>
          <span style={{ color: "#64748b", marginRight: "8px" }}>
            期待結果:
          </span>
          <span>{step.expect || "期待結果が入力されていません"}</span>
        </div>

        {/* テストターゲット表示 */}
        <div>
          <span style={{ color: "#64748b", marginRight: "8px" }}>
            テストターゲット:
          </span>
          <div
            style={{
              color:
                step.action_detail?.test_targets &&
                step.action_detail.test_targets.length > 0
                  ? "inherit"
                  : "#d1d5db",
              fontStyle:
                step.action_detail?.test_targets &&
                step.action_detail.test_targets.length > 0
                  ? "normal"
                  : "italic",
            }}
          >
            {step.action_detail?.test_targets &&
            step.action_detail.test_targets.length > 0
              ? step.action_detail.test_targets.map((tt, idx) => (
                  <span
                    key={`${tt.test_target_id}-${idx}`}
                    style={{
                      display: "inline-block",
                      margin: "2px 4px 2px 0",
                    }}
                  >
                    <button
                      style={{
                        padding: "2px 6px",
                        fontSize: "0.65rem",
                        background: "#e0f2fe",
                        color: "#0369a1",
                        border: "1px solid #bae6fd",
                        borderRadius: "3px",
                        cursor: "default",
                      }}
                    >
                      {tt.name}
                    </button>
                    {idx < (step.action_detail?.test_targets?.length || 0) - 1
                      ? ", "
                      : ""}
                  </span>
                ))
              : "テストターゲットが設定されていません"}
          </div>
        </div>
      </div>
      {renderModalsAndWindows()}
      </>
    );
  }

  if (layout === "compact") {
    return (
      <>
      <div className={className} style={style}>
        {/* 上段: 手順と期待結果 */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "6px",
          }}
        >
          <input
            value={step.step || ""}
            onChange={(e) => onUpdateStep?.("step", e.target.value)}
            placeholder="手順"
            style={{
              fontSize: "0.8rem",
              flex: "2",
              padding: "4px 6px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
            }}
          />
          <input
            value={step.expect || ""}
            onChange={(e) => onUpdateStep?.("expect", e.target.value)}
            placeholder="期待結果"
            style={{
              fontSize: "0.8rem",
              flex: "1",
              padding: "4px 6px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
            }}
          />
        </div>

        {/* 下段: アクションとパラメータ */}
        <div
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* アクション選択 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {/* ヘルプアイコン */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                setHelpAnchorEl(e.currentTarget);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <HelpOutlineIcon
                style={{
                  fontSize: "1rem",
                  color: "#64748b",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.color = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.color = "#64748b";
                }}
              />
            </div>
            <div
              style={{
                position: "relative",
                minWidth: "80px",
                maxWidth: "120px",
              }}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  const newState = !showActionDropdown;
                  setShowActionDropdown(newState);
                  // グローバル状態を更新
                  if (newState && stepIndex !== undefined) {
                    onGlobalDropdownChange?.({
                      stepIndex,
                      dropdownType: 'action'
                    });
                  } else {
                    onGlobalDropdownChange?.({
                      stepIndex: null,
                      dropdownType: null
                    });
                  }
                }}
                style={{
                  padding: "2px 4px",
                  fontSize: "0.7rem",
                  cursor: "pointer",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  background: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{step.action_detail?.step_action?.action || "click"}</span>
                <ExpandMoreIcon style={{ fontSize: "0.8rem" }} />
              </div>
              {showActionDropdown && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "white",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  zIndex: 1000,
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {getAvailableActions().map((action) => {
                  const isSelected =
                    step.action_detail?.step_action?.action === action;

                  return (
                    <div
                      key={action}
                      onClick={() => {
                        onUpdateAction?.(action);
                        setShowActionDropdown(false);
                      }}
                      style={{
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: "0.7rem",
                        background: isSelected ? "#f3f4f6" : "transparent",
                        color: isSelected ? "#1f2937" : "#6b7280",
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.background = "#f3f4f6";
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.background = isSelected
                          ? "#f3f4f6"
                          : "transparent";
                      }}
                    >
                      {action}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </div>

          {/* パラメータ表示（コンパクト版） */}
          {step.action_detail?.step_action &&
            getActionConfig(step.action_detail.step_action.action)
              ?.parameters &&
            getActionConfig(
              step.action_detail.step_action.action
            )!.parameters.map((paramConfig) => {
              const existingParam = step.action_detail?.step_action?.parameters.find(
                (p) => p.key === paramConfig.name
              );
              const paramValue = existingParam?.value || "";

              if (paramConfig.type === "test_target") {
                const targetEntry = step.action_detail?.test_targets?.find(
                  (tt) => tt.id === paramValue
                );
                const actualTargetId = targetEntry?.test_target_id;
                const targetName = actualTargetId
                  ? localTestTargets.find((t) => t.id === actualTargetId)?.name || actualTargetId
                  : paramConfig.required ? `${paramConfig.name} *(必須)` : paramConfig.name;
                const isRequired = paramConfig.required === true;
                const isEmpty = !paramValue || paramValue.trim() === "";

                return (
                  <div
                    key={paramConfig.name}
                    style={{ position: "relative", minWidth: "100px" }}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActionDropdown(false);
                        setCurrentTargetParam(paramConfig.name);
                        setShowTargetModal(true);
                      }}
                      style={{
                        padding: "2px 4px",
                        fontSize: "0.7rem",
                        cursor: "pointer",
                        border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                        borderRadius: "4px",
                        background: isRequired ? "white" : "#f9fafb",
                      }}
                    >
                      <span style={{ 
                        color: isEmpty ? "#64748b" : undefined
                      }}>
                        {targetName}
                      </span>
                    </div>
                  </div>
                );
              } else {
                // elementパラメータの場合はコンボボックス
                if (paramConfig.name === "element") {
                  const candidates = getElementCandidates();
                  const inputValue = elementInputValues[paramConfig.name] ?? paramValue;
                  const filteredCandidates = inputValue
                    ? candidates.filter((c) =>
                        c.toLowerCase().includes(inputValue.toLowerCase())
                      )
                    : candidates;
                  const showDropdown = showElementDropdowns[paramConfig.name] && filteredCandidates.length > 0;
                  const isRequired = paramConfig.required === true;
                  const isEmpty = !inputValue || inputValue.trim() === "";

                  return (
                    <div
                      key={paramConfig.name}
                      style={{ 
                        position: "relative", 
                        minWidth: "80px",
                        // プルダウンが開いているときに親要素のスタックコンテキストを上位に配置
                        zIndex: showDropdown ? 10001 : undefined
                      }}
                    >
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setElementInputValues({
                            ...elementInputValues,
                            [paramConfig.name]: newValue,
                          });
                          onUpdateParameter?.(paramConfig.name, newValue);
                          setShowElementDropdowns({
                            ...showElementDropdowns,
                            [paramConfig.name]: true,
                          });
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                          // setTimeoutで少し遅らせて、handleClickOutsideが先に発火しないようにする
                          setTimeout(() => {
                            setShowElementDropdowns({
                              ...showElementDropdowns,
                              [paramConfig.name]: true,
                            });
                          }, 0);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (candidates.length > 0) {
                            setShowElementDropdowns({
                              ...showElementDropdowns,
                              [paramConfig.name]: true,
                            });
                          }
                        }}
                        placeholder={isRequired ? `${paramConfig.name} *(必須)` : paramConfig.name}
                        style={{
                          minWidth: "80px",
                          padding: "2px 4px",
                          border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          background: isRequired ? "white" : "#f9fafb",
                        }}
                      />
                      {showDropdown && (
                        <div
                          data-element-dropdown
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "white",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                            zIndex: 10000,
                            maxHeight: "150px",
                            overflowY: "auto",
                            marginTop: "2px",
                          }}
                        >
                          {filteredCandidates.map((candidate) => (
                            <div
                              key={candidate}
                              onClick={() => {
                                setElementInputValues({
                                  ...elementInputValues,
                                  [paramConfig.name]: candidate,
                                });
                                onUpdateParameter?.(paramConfig.name, candidate);
                                setShowElementDropdowns({
                                  ...showElementDropdowns,
                                  [paramConfig.name]: false,
                                });
                              }}
                              style={{
                                padding: "4px 8px",
                                cursor: "pointer",
                                fontSize: "0.65rem",
                                color: "#374151",
                              }}
                              onMouseEnter={(e) => {
                                (e.target as HTMLElement).style.background =
                                  "#f3f4f6";
                              }}
                              onMouseLeave={(e) => {
                                (e.target as HTMLElement).style.background =
                                  "transparent";
                              }}
                            >
                              {candidate}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                // optionsが存在する場合はプルダウン
                if (paramConfig.options) {
                  const options = paramConfig.options.split(',').map(opt => opt.trim());
                  const isRequired = paramConfig.required === true;
                  const isEmpty = !paramValue || paramValue.trim() === "";

                  return (
                    <div
                      key={paramConfig.name}
                      style={{ position: "relative", minWidth: "80px" }}
                    >
                      <select
                        value={paramValue}
                        onChange={(e) =>
                          onUpdateParameter?.(paramConfig.name, e.target.value)
                        }
                        style={{
                          minWidth: "80px",
                          padding: "2px 4px",
                          border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          background: isRequired ? "white" : "#f9fafb",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">{isRequired ? `${paramConfig.name} *(必須)` : paramConfig.name}</option>
                        {options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }
                // その他のパラメータは通常のinput（valueType === 'number' のときは半角数字のみ）
                const isRequired = paramConfig.required === true;
                const isEmpty = !paramValue || paramValue.trim() === "";
                const isNumeric = paramConfig.valueType === "number";
                const allowNegative = paramConfig.allowNegative === true;
                const displayValue = isNumeric
                  ? filterNumericInput(paramValue, allowNegative)
                  : paramValue;

                return (
                  <input
                    key={paramConfig.name}
                    type="text"
                    inputMode={isNumeric ? "numeric" : "text"}
                    value={displayValue}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const next = isNumeric
                        ? filterNumericInput(raw, allowNegative)
                        : raw;
                      onUpdateParameter?.(paramConfig.name, next);
                    }}
                    placeholder={
                      isNumeric
                        ? isRequired
                          ? `${paramConfig.name} *(必須・半角数字のみ)`
                          : `${paramConfig.name}（半角数字のみ）`
                        : isRequired
                          ? `${paramConfig.name} *(必須)`
                          : paramConfig.name
                    }
                    style={{
                      minWidth: "80px",
                      padding: "2px 4px",
                      border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "0.7rem",
                      background: isRequired ? "white" : "#f9fafb",
                    }}
                  />
                );
              }
            })}
        </div>
      </div>
      {renderModalsAndWindows()}
      </>
    );
  }

  return (
    <>
    <div className={className} style={style}>
      {/* 操作内容 */}
      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            display: "block",
            fontSize: "0.75rem",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "4px",
          }}
        >
          操作内容:
        </label>
        <textarea
          value={step.step || ""}
          onChange={(e) => onUpdateStep?.("step", e.target.value)}
          placeholder="操作内容を入力してください"
          style={{
            width: "100%",
            padding: "6px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "0.75rem",
            minHeight: "60px",
            resize: "vertical",
          }}
        />
      </div>

      {/* 期待結果 */}
      <div style={{ marginBottom: "12px" }}>
        <label
          style={{
            display: "block",
            fontSize: "0.75rem",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "4px",
          }}
        >
          期待結果:
        </label>
        <textarea
          value={step.expect || ""}
          onChange={(e) => onUpdateStep?.("expect", e.target.value)}
          placeholder="期待結果を入力してください"
          style={{
            width: "100%",
            padding: "6px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "0.75rem",
            minHeight: "60px",
            resize: "vertical",
          }}
        />
      </div>

      {/* アクション選択 */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            marginBottom: "4px",
          }}
        >
          <label
            style={{
              fontSize: "0.75rem",
              fontWeight: "500",
              color: "#374151",
            }}
          >
            アクション:
          </label>
          {/* ヘルプアイコン */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setHelpAnchorEl(e.currentTarget);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <HelpOutlineIcon
              style={{
                fontSize: "1rem",
                color: "#64748b",
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.color = "#3b82f6";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.color = "#64748b";
              }}
            />
          </div>
        </div>
        <div style={{ position: "relative" }}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              const newState = !showActionDropdown;
              setShowActionDropdown(newState);
              // グローバル状態を更新
              if (newState && stepIndex !== undefined) {
                onGlobalDropdownChange?.({
                  stepIndex,
                  dropdownType: 'action'
                });
              } else {
                onGlobalDropdownChange?.({
                  stepIndex: null,
                  dropdownType: null
                });
              }
            }}
            style={{
              padding: "6px 12px",
              minWidth: "120px",
              fontSize: "0.75rem",
              cursor: "pointer",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              background: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{step.action_detail?.step_action?.action || "click"}</span>
            <ExpandMoreIcon
              style={{
                fontSize: "14px",
                marginLeft: "4px",
                transform: showActionDropdown
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </div>
          {showActionDropdown && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                background: "white",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                zIndex: 10000,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {getAvailableActions().map((action) => {
                const isSelected =
                  step.action_detail?.step_action?.action === action;

                return (
                  <div
                    key={action}
                    onClick={() => {
                      onUpdateAction?.(action);
                      setShowActionDropdown(false);
                    }}
                    style={{
                      padding: "6px 12px",
                      cursor: "pointer",
                      fontSize: "0.75rem",
                      background: isSelected ? "#f3f4f6" : "transparent",
                      color: isSelected ? "#1f2937" : "#6b7280",
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = "#f3f4f6";
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = isSelected
                        ? "#f3f4f6"
                        : "transparent";
                    }}
                  >
                    {action}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* パラメータ設定 */}
      {step.action_detail?.step_action &&
        getActionConfig(step.action_detail.step_action.action)?.parameters &&
        getActionConfig(step.action_detail.step_action.action)!.parameters
          .length > 0 && (
          <div style={{ marginBottom: "12px" }}>
            <label
              style={{
                display: "block",
                fontSize: "0.75rem",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              パラメータ設定:
            </label>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {getActionConfig(
                step.action_detail!.step_action!.action
              )!.parameters.map((paramConfig) => {
                const existingParam = step.action_detail?.step_action?.parameters.find(
                  (p) => p.key === paramConfig.name
                );
                const paramValue = existingParam?.value || "";

                if (paramConfig.type === "test_target") {
                  const targetEntry = step.action_detail?.test_targets?.find(
                    (tt) => tt.id === paramValue
                  );
                  const actualTargetId = targetEntry?.test_target_id;
                  const targetName = actualTargetId
                    ? localTestTargets.find((t) => t.id === actualTargetId)?.name || actualTargetId
                    : paramConfig.required ? `${paramConfig.name} *(必須)` : "テストターゲットを選択";
                  const isRequired = paramConfig.required === true;
                  const isEmpty = !paramValue || paramValue.trim() === "";

                  return (
                    <div key={paramConfig.name}>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {paramConfig.name}:
                      </div>
                      <div style={{ position: "relative" }}>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionDropdown(false);
                            setCurrentTargetParam(paramConfig.name);
                            setShowTargetModal(true);
                          }}
                          style={{
                            padding: "4px 8px",
                            fontSize: "0.7rem",
                            minWidth: "200px",
                            cursor: "pointer",
                            border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                            borderRadius: "4px",
                            background: isRequired ? "white" : "#f9fafb",
                          }}
                        >
                          <span style={{ 
                            color: isEmpty ? "#64748b" : undefined
                          }}>
                            {targetName}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // elementパラメータの場合はコンボボックス
                  if (paramConfig.name === "element") {
                    const candidates = getElementCandidates();
                    const inputValue = elementInputValues[paramConfig.name] ?? paramValue;
                    const filteredCandidates = inputValue
                      ? candidates.filter((c) =>
                          c.toLowerCase().includes(inputValue.toLowerCase())
                        )
                      : candidates;
                    const showDropdown = showElementDropdowns[paramConfig.name] && filteredCandidates.length > 0;
                    const isRequired = paramConfig.required === true;
                    const isEmpty = !inputValue || inputValue.trim() === "";

                    return (
                      <div key={paramConfig.name}>
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          {paramConfig.name}:
                        </div>
                        <div style={{ position: "relative" }}>
                          <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setElementInputValues({
                                ...elementInputValues,
                                [paramConfig.name]: newValue,
                              });
                              onUpdateParameter?.(paramConfig.name, newValue);
                              setShowElementDropdowns({
                                ...showElementDropdowns,
                                [paramConfig.name]: true,
                              });
                            }}
                            onFocus={(e) => {
                              e.stopPropagation();
                              // setTimeoutで少し遅らせて、handleClickOutsideが先に発火しないようにする
                              setTimeout(() => {
                                setShowElementDropdowns({
                                  ...showElementDropdowns,
                                  [paramConfig.name]: true,
                                });
                              }, 0);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (candidates.length > 0) {
                                setShowElementDropdowns({
                                  ...showElementDropdowns,
                                  [paramConfig.name]: true,
                                });
                              }
                            }}
                            placeholder={isRequired ? `${paramConfig.name} *(必須)` : `${paramConfig.name}を入力`}
                            style={{
                              width: "100%",
                              padding: "4px 6px",
                              border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                              borderRadius: "3px",
                              fontSize: "0.7rem",
                              background: isRequired ? "white" : "#f9fafb",
                            }}
                          />
                          {showDropdown && (
                            <div
                              data-element-dropdown
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                background: "white",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                zIndex: 1000,
                                maxHeight: "150px",
                                overflowY: "auto",
                                marginTop: "2px",
                              }}
                            >
                              {filteredCandidates.map((candidate) => (
                                <div
                                  key={candidate}
                                  onClick={() => {
                                    setElementInputValues({
                                      ...elementInputValues,
                                      [paramConfig.name]: candidate,
                                    });
                                    onUpdateParameter?.(paramConfig.name, candidate);
                                    setShowElementDropdowns({
                                      ...showElementDropdowns,
                                      [paramConfig.name]: false,
                                    });
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    cursor: "pointer",
                                    fontSize: "0.7rem",
                                    color: "#374151",
                                  }}
                                  onMouseEnter={(e) => {
                                    (e.target as HTMLElement).style.background =
                                      "#f3f4f6";
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.target as HTMLElement).style.background =
                                      "transparent";
                                  }}
                                >
                                  {candidate}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  // optionsが存在する場合はプルダウン
                  if (paramConfig.options) {
                    const options = paramConfig.options.split(',').map(opt => opt.trim());
                    const isRequired = paramConfig.required === true;
                    const isEmpty = !paramValue || paramValue.trim() === "";

                    return (
                      <div key={paramConfig.name}>
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          {paramConfig.name}:
                        </div>
                        <select
                          value={paramValue}
                          onChange={(e) =>
                            onUpdateParameter?.(paramConfig.name, e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                            borderRadius: "3px",
                            fontSize: "0.7rem",
                            background: isRequired ? "white" : "#f9fafb",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">{isRequired ? `${paramConfig.name} *(必須)` : `${paramConfig.name}を選択`}</option>
                          {options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  // その他のパラメータは通常のinput（valueType === 'number' のときは半角数字のみ）
                  const isRequired = paramConfig.required === true;
                  const isEmpty = !paramValue || paramValue.trim() === "";
                  const isNumeric = paramConfig.valueType === "number";
                  const allowNegative = paramConfig.allowNegative === true;
                  const displayValue = isNumeric
                    ? filterNumericInput(paramValue, allowNegative)
                    : paramValue;

                  return (
                    <div key={paramConfig.name}>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#6b7280",
                          marginBottom: "4px",
                        }}
                      >
                        {paramConfig.name}:
                      </div>
                      <input
                        type="text"
                        inputMode={isNumeric ? "numeric" : "text"}
                        value={displayValue}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const next = isNumeric
                            ? filterNumericInput(raw, allowNegative)
                            : raw;
                          onUpdateParameter?.(paramConfig.name, next);
                        }}
                        placeholder={
                          isNumeric
                            ? isRequired
                              ? `${paramConfig.name} *(必須・半角数字のみ)`
                              : `${paramConfig.name}（半角数字のみ）`
                            : isRequired
                              ? `${paramConfig.name} *(必須)`
                              : `${paramConfig.name}を入力`
                        }
                        style={{
                          width: "100%",
                          padding: "4px 6px",
                          border: isRequired && isEmpty ? "1px solid #fca5a5" : "1px solid #d1d5db",
                          borderRadius: "3px",
                          fontSize: "0.7rem",
                          background: isRequired ? "white" : "#f9fafb",
                        }}
                      />
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}
    </div>
    {renderModalsAndWindows()}
    </>
  );
};
